import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
interface TemperatureData {
  timestamp: string;
  temperature: number;
  unit: string;
  status: string;
  deviceName?: string;
}

interface TemperatureChartTabProps {
  deviceId: string;
  includeChildren?: boolean;
}

const TemperatureChartTab: React.FC<TemperatureChartTabProps> = ({ deviceId, includeChildren = false }) => {
  const auth = useAuth();
  const theme = useTheme();
  const accessToken = auth.tokens?.accessToken;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [temperatureData, setTemperatureData] = useState<TemperatureData[]>([]);
  const [timeRange, setTimeRange] = useState<string>('30d');
  
  const authHeader = useMemo(() => ({ Authorization: `Bearer ${accessToken}` }), [accessToken]);

  const loadTemperatureData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch more events to get temperature data
      const params = new URLSearchParams({ skip: '0', take: '1000', sortBy: 'dateCreated', sortOrder: 'desc' });
      if (includeChildren) params.append('includeChildren', 'true');
      const res = await fetch(
        `/api/devices/${deviceId}/events?${params}`, 
        { headers: authHeader }
      );
      if (!res.ok) throw new Error('Failed to fetch events');
      
      const responseData = await res.json();
      const events = responseData.data || [];
      
      // Filter and parse temperature events
      const tempData: TemperatureData[] = [];
      
      for (const event of events) {
        try {
          let parsedData: any = null;
          if (event.name) {
            try { parsedData = JSON.parse(event.name); } catch { /* Not JSON */ }
          }
          if (parsedData && parsedData.data && typeof parsedData.data.temperature === 'number') {
            tempData.push({
              timestamp: event.dateCreated,
              temperature: parsedData.data.temperature,
              unit: parsedData.data.unit || 'Unknown',
              status: parsedData.data.status || 'Unknown',
              deviceName: event.deviceName || undefined,
            });
          }
        } catch (e) {
          continue;
        }
      }
      
      // Filter by time range
      const now = new Date();
      const filtered = tempData.filter(item => {
        const itemDate = new Date(item.timestamp);
        const diff = now.getTime() - itemDate.getTime();
        
        switch (timeRange) {
          case '1h':
            return diff <= 60 * 60 * 1000;
          case '6h':
            return diff <= 6 * 60 * 60 * 1000;
          case '24h':
            return diff <= 24 * 60 * 60 * 1000;
          case '7d':
            return diff <= 7 * 24 * 60 * 60 * 1000;
          case '30d':
            return diff <= 30 * 24 * 60 * 60 * 1000;
          default:
            return true;
        }
      });
      
      // Sort by timestamp ascending for chart
      filtered.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      
      setTemperatureData(filtered);
    } catch (e: any) {
      setError(e.message || 'Failed to load temperature data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemperatureData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, timeRange, includeChildren]);

  // Unique device names (for multi-device lines when includeChildren)
  const deviceNames = useMemo(() => {
    const names = new Set<string>();
    temperatureData.forEach(d => { if (d.deviceName) names.add(d.deviceName); });
    return Array.from(names);
  }, [temperatureData]);

  const DEVICE_COLORS = ['#4fc3f7', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#f97316'];

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const chartData = useMemo(() => {
    // Sort by timestamp ascending for chart
    const sorted = [...temperatureData].sort((a, b) =>
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
    if (includeChildren && deviceNames.length > 1) {
      // Group data points by timestamp bucket; each device gets its own key
      return sorted.map(item => ({
        timestamp: new Date(item.timestamp).toLocaleTimeString(),
        fullTimestamp: item.timestamp,
        [item.deviceName || 'Unknown']: item.temperature,
        unit: item.unit,
        status: item.status,
      }));
    }
    return sorted.map(item => ({
      timestamp: new Date(item.timestamp).toLocaleTimeString(),
      fullTimestamp: item.timestamp,
      temperature: item.temperature,
      unit: item.unit,
      status: item.status,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temperatureData, includeChildren, deviceNames]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Temperature Chart</Typography>
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="1h">Last Hour</MenuItem>
            <MenuItem value="6h">Last 6 Hours</MenuItem>
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : temperatureData.length === 0 ? (
        <Alert severity="info">
          No temperature data available for this device in the selected time range.
        </Alert>
      ) : (
        <Box>
          <ResponsiveContainer width="100%" height={500}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="timestamp" 
                label={{ value: 'Time', position: 'insideBottom', offset: -5 }}
              />
              <YAxis 
                label={{ value: `Temperature (${temperatureData[0]?.unit || '°C'})`, angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <Box sx={{ bgcolor: 'background.paper', p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                        <Typography variant="body2"><strong>Time:</strong> {formatTimestamp(data.fullTimestamp)}</Typography>
                        {payload.map((p: any, i: number) => (
                          <Typography key={i} variant="body2" sx={{ color: p.color }}>
                            <strong>{p.name}:</strong> {p.value} {data.unit}
                          </Typography>
                        ))}
                        {data.status && <Typography variant="body2"><strong>Status:</strong> {data.status}</Typography>}
                      </Box>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              {includeChildren && deviceNames.length > 1
                ? deviceNames.map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={DEVICE_COLORS[i % DEVICE_COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      name={name}
                      connectNulls
                    />
                  ))
                : (
                  <Line 
                    type="monotone" 
                    dataKey="temperature" 
                    stroke="#4fc3f7" 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                    name="Temperature"
                  />
                )
              }
            </LineChart>
          </ResponsiveContainer>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {temperatureData.length} temperature readings
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TemperatureChartTab;
